import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {useState} from 'react';
import {t} from 'i18next';
import {
  CHAR_LIMIT_500,
  PROJECT_ID_LIMIT_40,
} from '../../../constants/constants';

interface ProjectProfileEditableDetailsProps {
  projectDescription: string;
  setProjectDescription: (val: string) => void;

  overseeingAttorneys: string;
  setOverseeingAttorneys: (val: string) => void;

  entityName: string;
  setEntityName: (val: string) => void;

  clientUniqueProjectId: string;
  setClientUniqueProjectId: (val: string) => void;

  overseeingAttorneysEmail: string;
  setOverseeingAttorneysEmail: (val: string) => void;

  additionalContactInfo: string;
  setAdditionalContactInfo: (val: string) => void;

  internalProjectName: string;
  setInternalProjectName: (val: string) => void;

  hasError: (data: boolean) => void;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const ProjectProfileEditableDetails = ({
  projectDescription,
  setProjectDescription,
  overseeingAttorneys,
  setOverseeingAttorneys,
  entityName,
  setEntityName,
  clientUniqueProjectId,
  setClientUniqueProjectId,
  overseeingAttorneysEmail,
  setOverseeingAttorneysEmail,
  additionalContactInfo,
  setAdditionalContactInfo,
  internalProjectName,
  setInternalProjectName,
  hasError,
}: ProjectProfileEditableDetailsProps) => {
  const [errors, setErrors] = useState({
    projectDescription: false,
    overseeingAttorneys: false,
    entityName: false,
    clientUniqueProjectId: false,
    overseeingAttorneysEmail: false,
    additionalContactInfo: false,
    internalProjectName: false,
  });

  const getHelperText = () =>
    t('clientDetailsPage.projectDrawer.charLimitValidHelperText');

  const validateEmails = (input: string): boolean => {
    if (input.length > CHAR_LIMIT_500) return false;
    return input
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)
      .every((email) => EMAIL_REGEX.test(email));
  };

  const checkAllErrors = (field: keyof typeof errors, newVal: string) => {
    const newErrors = {...errors};

    if (field === 'overseeingAttorneysEmail') {
      newErrors[field] = !validateEmails(newVal);
    } else if (
      field === 'clientUniqueProjectId' ||
      field === 'internalProjectName'
    ) {
      newErrors[field] = newVal.length > PROJECT_ID_LIMIT_40;
    } else {
      newErrors[field] = newVal.length > CHAR_LIMIT_500;
    }

    setErrors(newErrors);
    hasError(Object.values(newErrors).some(Boolean));
  };

  return (
    <Box className="profile-edit-content">
      {/* Project Description */}
      <Box mt={2}>
        <Typography className="project-edit-header" mb={1}>
          {t('clientDetailsPage.projectDrawer.description')}
        </Typography>

        <AurTextField
          fullWidth
          multiline
          rows={5}
          error={errors.projectDescription}
          helperText={getHelperText()}
          InputProps={{
            endAdornment: <InputAdornment position="end" />,
          }}
          onChange={(e) => {
            const value = e.target.value;
            setProjectDescription(value);
            checkAllErrors('projectDescription', value);
          }}
          value={projectDescription}
          label={t('clientDetailsPage.projectDrawer.projectDescription')}
          variant="filled"
          className="aur-textfield custom-text-field resizableTextField project-profile-edit-textfield"
          ariaLabel={t('clientDetailsPage.projectDrawer.projectDescription')}
        />
      </Box>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Typography className="project-edit-header" mb={1}>
            {t('clientDetailsPage.projectDrawer.entityName')}
          </Typography>

          <AurTextField
            fullWidth
            error={errors.entityName}
            helperText={getHelperText()}
            InputProps={{endAdornment: <InputAdornment position="end" />}}
            onChange={(e) => {
              const value = e.target.value;
              setEntityName(value);
              checkAllErrors('entityName', value);
            }}
            value={entityName}
            label={t('clientDetailsPage.projectDrawer.entityName')}
            variant="filled"
            className="aur-textfield custom-text-field project-profile-edit-textfield"
            ariaLabel={t('clientDetailsPage.projectDrawer.entityName')}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={6} mt={2}>
          <Typography className="project-edit-header" mb={1}>
            {t('welcomeScreenSingleClient.InternalProjectName')}
          </Typography>

          <AurTextField
            fullWidth
            error={errors.internalProjectName}
            helperText={t(
              'clientDetailsPage.projectDrawer.projectIdCharLimitValidHelperText'
            )}
            InputProps={{
              endAdornment: <InputAdornment position="end" />,
            }}
            onChange={(e) => {
              const value = e.target.value;
              setInternalProjectName(value);
              checkAllErrors('internalProjectName', value);
            }}
            value={internalProjectName}
            label={t('welcomeScreenSingleClient.InternalProjectName')}
            variant="filled"
            className="aur-textfield custom-text-field project-profile-edit-textfield"
            ariaLabel={t('welcomeScreenSingleClient.InternalProjectName')}
          />
        </Grid>

        <Grid item xs={12} md={6} mt={2}>
          <Typography className="project-edit-header" mb={1}>
            {t('clientDetailsPage.projectDrawer.clientUniqueProjectProfileId')}
          </Typography>

          <AurTextField
            fullWidth
            error={errors.clientUniqueProjectId}
            helperText={t(
              'clientDetailsPage.projectDrawer.projectIdCharLimitValidHelperText'
            )}
            InputProps={{endAdornment: <InputAdornment position="end" />}}
            onChange={(e) => {
              const value = e.target.value;
              setClientUniqueProjectId(value);
              checkAllErrors('clientUniqueProjectId', value);
            }}
            value={clientUniqueProjectId}
            label={t('clientDetailsPage.projectDrawer.clientUniqueProjectId')}
            variant="filled"
            className="aur-textfield custom-text-field project-profile-edit-textfield"
            ariaLabel={t(
              'clientDetailsPage.projectDrawer.clientUniqueProjectId'
            )}
          />
        </Grid>
      </Grid>

      {/* Overseeing Attorney Info */}
      <Box mt={4}>
        <Typography mb={1} className="project-edit-header">
          {t('clientDetailsPage.projectDrawer.overseingAttorney')}
        </Typography>

        <AurTextField
          fullWidth
          multiline
          rows={5}
          error={errors.overseeingAttorneys}
          helperText={getHelperText()}
          InputProps={{endAdornment: <InputAdornment position="end" />}}
          onChange={(e) => {
            const value = e.target.value;
            setOverseeingAttorneys(value);
            checkAllErrors('overseeingAttorneys', value);
          }}
          value={overseeingAttorneys}
          label={t('clientDetailsPage.projectDrawer.overseeingAttorneyNames')}
          variant="filled"
          className="aur-textfield custom-text-field resizableTextField project-profile-edit-textfield"
          ariaLabel={t(
            'clientDetailsPage.projectDrawer.overseeingAttorneyNames'
          )}
        />
      </Box>

      <Box mt={3}>
        <AurTextField
          fullWidth
          multiline
          rows={5}
          error={errors.overseeingAttorneysEmail}
          helperText={t(
            'clientDetailsPage.projectDrawer.overseeingAttorneyEmailValidHelperText'
          )}
          InputProps={{endAdornment: <InputAdornment position="end" />}}
          onChange={(e) => {
            const value = e.target.value;
            setOverseeingAttorneysEmail(value);
            checkAllErrors('overseeingAttorneysEmail', value);
          }}
          value={overseeingAttorneysEmail}
          label={t('clientDetailsPage.projectDrawer.emailAddresses')}
          variant="filled"
          className="aur-textfield custom-text-field resizableTextField project-profile-edit-textfield"
          ariaLabel={t('clientDetailsPage.projectDrawer.emailAddresses')}
        />
      </Box>

      <Box mt={3}>
        <AurTextField
          fullWidth
          multiline
          rows={5}
          error={errors.additionalContactInfo}
          helperText={getHelperText()}
          InputProps={{endAdornment: <InputAdornment position="end" />}}
          onChange={(e) => {
            const value = e.target.value;
            setAdditionalContactInfo(value);
            checkAllErrors('additionalContactInfo', value);
          }}
          value={additionalContactInfo}
          label={t(
            'clientDetailsPage.projectDrawer.additionalContactInformation'
          )}
          variant="filled"
          className="aur-textfield custom-text-field resizableTextField project-profile-edit-textfield"
          ariaLabel={t(
            'clientDetailsPage.projectDrawer.additionalContactInformation'
          )}
        />
      </Box>
    </Box>
  );
};

export default ProjectProfileEditableDetails;
